/**
 * Wire frames and the cursor rule for the mount push stream.
 * See Caveat_WebCanvasFramesDuplicatesTheOverlayCursorRule.
 */

import type {
  BackpressureNotice,
  CanvasArrangement,
  CueFrame,
  DeltaFrame,
  Entity,
  OverlayWelcome,
  QueuedNotice,
  RegionMap,
  ResyncRequest,
  SceneDocument,
  SnapshotFrame,
  SnapshotPointer,
  StateDelta,
} from "@nebulize/sdk";

import type { AttributeValue, CanvasState, StateEntity, TextValue } from "./runtime";

/** The four frames that carry (or deliberately do not carry) the cursor. */
export type StateFrame = OverlayWelcome | SnapshotFrame | DeltaFrame | CueFrame;

/** Everything the server may send down the stream. */
export type InboundFrame = StateFrame | SnapshotPointer | BackpressureNotice | QueuedNotice;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSequence(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isEpoch(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function isAbsentOrArray(value: unknown): boolean {
  return value === undefined || isArray(value);
}

/** The instant a frame says it was sent, absent on a body retained rather than pushed. */
function sentAt(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isStringArray(value: unknown): value is string[] {
  return isArray(value) && value.every((member) => typeof member === "string");
}

function asWelcome(raw: Record<string, unknown>): OverlayWelcome | undefined {
  if (raw.mount_id !== null && raw.mount_id !== undefined && typeof raw.mount_id !== "string") {
    return undefined;
  }
  if (!isStringArray(raw.canvases)) return undefined;
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;

  return {
    frame: "welcome",
    mount_id: (raw.mount_id as string | null | undefined) ?? null,
    canvases: raw.canvases,
    sequence: raw.sequence,
    epoch: raw.epoch,
  };
}

function asSnapshot(raw: Record<string, unknown>): SnapshotFrame | undefined {
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;
  if (!isAbsentOrArray(raw.entities) || !isAbsentOrArray(raw.scenes)) return undefined;

  return {
    frame: "snapshot",
    sequence: raw.sequence,
    epoch: raw.epoch,
    sent_at: sentAt(raw.sent_at),
    entities: isArray(raw.entities) ? (raw.entities as Entity[]) : [],
    scenes: isArray(raw.scenes) ? (raw.scenes as SceneDocument[]) : [],
    regions: isArray(raw.regions) ? (raw.regions as RegionMap[]) : [],
    arrangement: asArrangement(raw.arrangement),
  };
}

/** The arrangement a snapshot carried, or nothing arranged (`OVL-027`).
 *
 * Shaped rather than trusted, the same way every other field on this frame is: an authority that
 * sends none, one that has never heard of the field, and one that sends something that is not an
 * object all read as unarranged, which is the state every space is in until somebody drags
 * something.
 */
function asArrangement(raw: unknown): CanvasArrangement {
  if (raw === null || typeof raw !== "object") return NOTHING_ARRANGED;
  const canvases = (raw as { canvases?: unknown }).canvases;
  if (canvases === null || typeof canvases !== "object") return NOTHING_ARRANGED;
  return { canvases: canvases as NonNullable<CanvasArrangement["canvases"]> };
}

function asDelta(raw: Record<string, unknown>): DeltaFrame | undefined {
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;
  if (!isAbsentOrArray(raw.deltas)) return undefined;

  return {
    frame: "delta",
    sequence: raw.sequence,
    epoch: raw.epoch,
    sent_at: sentAt(raw.sent_at),
    deltas: isArray(raw.deltas) ? (raw.deltas as StateDelta[]) : [],
  };
}

function asCue(raw: Record<string, unknown>): CueFrame | undefined {
  if (typeof raw.envelope_id !== "string" || raw.envelope_id.length === 0) return undefined;

  const cue = raw.cue;
  if (!isRecord(cue)) return undefined;
  if (typeof cue.tag !== "string" || cue.tag.length === 0) return undefined;
  if (!isStringArray(cue.canvases)) return undefined;

  return {
    frame: "cue",
    envelope_id: raw.envelope_id,
    cue: cue as unknown as CueFrame["cue"],
  };
}

function asSnapshotPointer(raw: Record<string, unknown>): SnapshotPointer | undefined {
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;
  if (typeof raw.url !== "string" || raw.url.length === 0) return undefined;

  return { frame: "snapshot_pointer", sequence: raw.sequence, epoch: raw.epoch, url: raw.url };
}

function asBackpressure(raw: Record<string, unknown>): BackpressureNotice | undefined {
  if (typeof raw.reason !== "string" || raw.reason.length === 0) return undefined;
  if (
    raw.retry_after_seconds !== undefined &&
    raw.retry_after_seconds !== null &&
    typeof raw.retry_after_seconds !== "number"
  ) {
    return undefined;
  }

  return {
    frame: "backpressure",
    reason: raw.reason,
    retry_after_seconds: (raw.retry_after_seconds as number | null | undefined) ?? null,
  };
}

/**
 * A place in the admission queue. Arrives before the welcome and never after it, because the
 * server only queues a connection it has not yet done any work for.
 */
function asQueued(raw: Record<string, unknown>): QueuedNotice | undefined {
  if (typeof raw.position !== "number" || !Number.isFinite(raw.position) || raw.position < 1) {
    return undefined;
  }

  return { frame: "queued", position: raw.position };
}

/** Take one JSON message as the frame its tag names, or as nothing at all. */
export function classifyFrame(raw: unknown): InboundFrame | undefined {
  if (!isRecord(raw)) return undefined;

  switch (raw.frame) {
    case "welcome":
      return asWelcome(raw);
    case "snapshot":
      return asSnapshot(raw);
    case "delta":
      return asDelta(raw);
    case "cue":
      return asCue(raw);
    case "snapshot_pointer":
      return asSnapshotPointer(raw);
    case "backpressure":
      return asBackpressure(raw);
    case "queued":
      return asQueued(raw);
    default:
      return undefined;
  }
}

/** Parse one socket message, treating anything unparseable as no frame at all. */
export function parseFrame(data: string): InboundFrame | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(data) as unknown;
  } catch {
    return undefined;
  }
  return classifyFrame(raw);
}

/** The last state frame this cursor took, and when it took it. See RUN-039. */
export interface FrameStamp {
  readonly epoch: string;
  readonly sequence: number;
  readonly sentAt: string;
  /** `performance.now()` when this cursor applied that frame. */
  readonly receivedAt: number;
}

/** Everything the cursor rule needs: where the store is, and what it currently holds. */
/** What an unarranged space sends, and what a cursor holds before its first snapshot. */
export const NOTHING_ARRANGED: CanvasArrangement = { canvases: {} };

export interface Cursor {
  readonly mountId: string;
  readonly sequence: number | undefined;
  readonly epoch: string | undefined;
  /** The origin a press counts from, absent until a frame naming its own send instant arrives. */
  readonly frame: FrameStamp | undefined;
  readonly canvases: readonly string[];
  readonly entities: ReadonlyMap<string, StateEntity>;
  /** What each activity draws, as the snapshot carried it. A delta never changes a document. */
  readonly scenes: readonly SceneDocument[];
  /** Where the cells of each declared region are, for a scene node that names one. */
  readonly regions: readonly RegionMap[];
  /** Where the customer put each activity on each canvas of this space. `OVL-027`. */
  readonly arrangement: CanvasArrangement;
}

export function emptyCursor(mountId: string): Cursor {
  return {
    mountId,
    sequence: undefined,
    epoch: undefined,
    frame: undefined,
    canvases: [],
    entities: new Map(),
    scenes: [],
    regions: [],
    arrangement: NOTHING_ARRANGED,
  };
}

/** The shape every existing caller of useCanvasState already reads. */
export function cursorState(cursor: Cursor): CanvasState {
  return {
    mount_id: cursor.mountId,
    activity_id: "",
    role: "",
    audiences: [],
    entities: [...cursor.entities.values()],
    canvases: [...cursor.canvases],
    scenes: [...cursor.scenes],
    regions: [...cursor.regions],
    arrangement: cursor.arrangement,
  };
}

function asAttributeValue(raw: { value: number; min?: number | null; max?: number | null }): AttributeValue {
  return { value: raw.value, min: raw.min ?? null, max: raw.max ?? null };
}

function asTextValues(raw: Record<string, { value: string }> | undefined): Record<string, TextValue> {
  const readings: Record<string, TextValue> = {};
  for (const [name, value] of Object.entries(raw ?? {})) readings[name] = { value: value.value };
  return readings;
}

function asStateEntity(entity: Entity): StateEntity {
  const attributes: Record<string, AttributeValue> = {};
  for (const [name, value] of Object.entries(entity.attributes ?? {})) {
    attributes[name] = asAttributeValue(value);
  }
  return {
    id: entity.id,
    type: entity.type,
    activity_id: entity.activity_id,
    attributes,
    text_attributes: asTextValues(entity.text_attributes),
    tags: { tags: entity.tags?.tags ?? [] },
    // Spread rather than assigned undefined, because `exactOptionalPropertyTypes` makes an
    // explicit undefined a different thing from an absent key, and a scene's `repeat` matches on
    // the absent one meaning "the default scope".
    ...(entity.scope === undefined ? {} : { scope: entity.scope }),
    ...(entity.facet === undefined ? {} : { facet: entity.facet }),
    // RUN-044. Carried through so a display read can evaluate the current value.
    ...(entity.modifiers === undefined ? {} : { modifiers: entity.modifiers }),
    // RUN-002. Carried through so a display read can show what is running.
    ...(entity.effects === undefined ? {} : { effects: entity.effects }),
  };
}

/** Whether a store at `cursor` adopts this snapshot. Another epoch always wins. */
export function snapshotSupersedes(frame: SnapshotFrame, cursor: Cursor): boolean {
  if (cursor.epoch !== undefined && cursor.epoch !== frame.epoch) return true;
  return cursor.sequence === undefined || frame.sequence > cursor.sequence;
}

/** Whether a store at `cursor` may apply this delta. False for an empty store. */
export function deltaFollows(frame: DeltaFrame, cursor: Cursor): boolean {
  if (cursor.epoch === undefined || cursor.epoch !== frame.epoch) return false;
  return cursor.sequence !== undefined && frame.sequence === cursor.sequence + 1;
}

/** Whether this delta is one the store already applied. Never true across an epoch. */
export function deltaIsRedelivery(frame: DeltaFrame, cursor: Cursor): boolean {
  if (cursor.epoch === undefined || cursor.epoch !== frame.epoch) return false;
  return cursor.sequence !== undefined && frame.sequence <= cursor.sequence;
}

function withDelta(entity: StateEntity, delta: StateDelta): StateEntity {
  const attributes: Record<string, AttributeValue> = { ...entity.attributes };
  for (const [name, value] of Object.entries(delta.attributes ?? {})) {
    attributes[name] = asAttributeValue(value);
  }
  // RUN-044: a delta carries a full replacement modifier set per named attribute, the same way
  // it carries a full replacement attribute reading, so this merges rather than accumulates.
  const modifiers = { ...entity.modifiers, ...delta.modifiers };
  // RUN-002: a delta names a started or restacked instance by its source, and an ended one in
  // `effects_ended` rather than by absence, so this merges the arrivals and then drops the
  // departures explicitly.
  const endedSources = new Set(delta.effects_ended ?? []);
  const effects = Object.fromEntries(
    Object.entries({ ...entity.effects, ...delta.effects }).filter(
      ([source]) => !endedSources.has(source),
    ),
  );
  const text: Record<string, TextValue> = {
    ...entity.text_attributes,
    ...asTextValues(delta.text_attributes),
  };
  const removed = new Set(delta.tags_removed ?? []);
  const kept = entity.tags.tags.filter((tag) => !removed.has(tag));
  const added = (delta.tags_added ?? []).filter((tag) => !removed.has(tag));
  return {
    ...entity,
    attributes,
    ...(Object.keys(modifiers).length === 0 ? {} : { modifiers }),
    effects,
    text_attributes: text,
    tags: { tags: [...new Set([...kept, ...added])] },
  };
}

/** The index the deltas produce, or undefined the moment one names an entity the store lacks. */
function applyDeltas(
  entities: ReadonlyMap<string, StateEntity>,
  deltas: readonly StateDelta[],
): Map<string, StateEntity> | undefined {
  const next = new Map(entities);
  for (const delta of deltas) {
    const id = delta.entity.entity_id;
    if (delta.removed === true) {
      next.delete(id);
      continue;
    }
    const entity = next.get(id);
    if (entity === undefined) return undefined;
    next.set(id, withDelta(entity, delta));
  }
  return next;
}

export type FrameOutcome =
  | { kind: "applied"; cursor: Cursor }
  | { kind: "resync"; reason: string };

/** The stamp a frame becomes, or the one already held when it names no send instant. */
function stampOf(frame: SnapshotFrame | DeltaFrame, cursor: Cursor, receivedAt?: number): FrameStamp | undefined {
  const sent = frame.sent_at ?? null;
  if (sent === null || receivedAt === undefined) return cursor.frame;
  return { epoch: frame.epoch, sequence: frame.sequence, sentAt: sent, receivedAt };
}

/** The cursor rule, pure and socket-free. See Caveat_WebCanvasFramesDuplicatesTheOverlayCursorRule. */
export function applyFrame(cursor: Cursor, frame: StateFrame, receivedAt?: number): FrameOutcome {
  switch (frame.frame) {
    case "welcome":
      return {
        kind: "applied",
        cursor: { ...cursor, mountId: frame.mount_id ?? cursor.mountId, canvases: frame.canvases },
      };

    case "snapshot": {
      if (!snapshotSupersedes(frame, cursor)) return { kind: "applied", cursor };
      const entities = new Map<string, StateEntity>();
      for (const entity of frame.entities ?? []) entities.set(entity.id, asStateEntity(entity));
      return {
        kind: "applied",
        cursor: {
          ...cursor,
          sequence: frame.sequence,
          epoch: frame.epoch,
          frame: stampOf(frame, cursor, receivedAt),
          entities,
          scenes: frame.scenes ?? [],
          regions: frame.regions ?? [],
          arrangement: frame.arrangement ?? NOTHING_ARRANGED,
        },
      };
    }

    case "delta": {
      if (deltaFollows(frame, cursor)) {
        const applied = applyDeltas(cursor.entities, frame.deltas ?? []);
        if (applied === undefined) return { kind: "resync", reason: "sequence gap" };
        return {
          kind: "applied",
          cursor: {
            ...cursor,
            sequence: frame.sequence,
            frame: stampOf(frame, cursor, receivedAt),
            entities: applied,
          },
        };
      }
      if (deltaIsRedelivery(frame, cursor)) return { kind: "applied", cursor };
      return { kind: "resync", reason: "sequence gap" };
    }

    case "cue":
      return { kind: "applied", cursor };
  }
}

export function resyncFrame(reason: string, cursor: Cursor): ResyncRequest {
  return { frame: "resync", reason, last_sequence: cursor.sequence ?? null, last_epoch: cursor.epoch ?? null };
}
